<template>
    <div class="">
        <template v-if="showLoginImage">
            <div class="min-h-screen flex flex-col lg:flex-row">
                <div
                    class="hidden lg:flex lg:w-1/2 items-center justify-center bg-theme-canvas p-6"
                >
                    <img
                        src="/img/login.webp"
                        loading="lazy"
                        class="max-w-md xl:max-w-lg w-full rounded-7 shadow-lg"
                        :alt="`Inicia sesión en ${$siteTitle}`"
                    />
                </div>
                <div
                    class="w-full lg:w-1/2 flex-1 px-2 py-8 lg:py-0 flex flex-col justify-center"
                >
                    <div
                        class="w-full sm:w-10/12 md:w-8/12 xl:w-9/12 mx-auto py-6 lg:py-2 flex flex-col justify-center mb-10"
                    >
                        <div
                            class="text-center py-4 lg:py-3 px-3 shadow-lg bg-body rounded-md"
                        >
                            <span
                                class="inline-block bg-stack-primary text-white text-xs font-semibold uppercase tracking-wide rounded-full px-3 py-1 mb-2"
                            >
                                Bienvenido de nuevo
                            </span>
                            <h1
                                class="text-login-headline font-bold text-theme-base text-center mb-1"
                            >
                                {{ slogan }}
                            </h1>
                            <p
                                class="text-theme-muted text-center text-sm mb-4"
                            >
                                Ingresa a tu cuenta
                                <span
                                    class="inline-block font-semibold text-white bg-theme-primary rounded-full px-2 py-0.5"
                                    >{{ $siteTitle }}</span
                                >
                            </p>
                            <small
                                v-if="showBrandFooter"
                                class="m-0 font-mono flex justify-center"
                            >
                                By&nbsp;
                                <a
                                    href="https://tucatalogo.co"
                                    target="_blank"
                                    rel="noopener"
                                >
                                    tucatalogo.co
                                </a>
                            </small>
                        </div>

                        <img
                            v-if="!autoRedirecting && !isLoading"
                            src="/img/logos/pet_logo.png"
                            loading="lazy"
                            class="mx-auto my-1 h-48 2xl:h-64 w-auto object-contain"
                            :alt="`Inicia sesión en ${$siteTitle}`"
                        />

                        <template v-if="!autoRedirecting">
                            <div
                                v-if="showFederatedSignIn"
                                class="flex flex-col items-center justify-center w-10/12 mx-auto h-28 px-2"
                            >
                                <app-button
                                    size="large"
                                    block
                                    border
                                    @click="loginGoogle"
                                >
                                    <img
                                        src="@/assets/images/google.svg"
                                        height="15"
                                        width="15"
                                        alt="reload img"
                                    />
                                    &nbsp; &nbsp;Continua con Google
                                </app-button>
                            </div>

                            <div v-else class="px-2">
                                <div
                                    v-if="!!error"
                                    class="bg-theme-danger border border-danger-light text-white p-2 mb-4 rounded"
                                >
                                    {{ error }}
                                </div>

                                <div class="mt-2">
                                    <custom-input
                                        id="user"
                                        v-model="user"
                                        type="text"
                                        name="user"
                                        :disabled="isLoading"
                                        label="Correo"
                                        aria-label="Correo"
                                        :error="
                                            !validUser && introUser
                                                ? 'Ingrese correo o usuario valido'
                                                : ''
                                        "
                                        @blur="introUser = true"
                                        @input="lowerCaseUser"
                                        @enter="loginPassword"
                                    >
                                        <template #icon> 🐵 </template>
                                    </custom-input>
                                </div>
                                <div class="mt-4">
                                    <custom-input
                                        v-model="password"
                                        :type="showPass ? 'text' : 'password'"
                                        label="Contraseña"
                                        aria-label="Contraseña"
                                        autocomplete="off"
                                        :disabled="isLoading"
                                        :error="
                                            !validPassword && introPassword
                                                ? 'Ingrese una contraseña valida'
                                                : ''
                                        "
                                        @click-icon="showPass = !showPass"
                                        @blur="introPassword = true"
                                        @enter="loginPassword"
                                    >
                                        <template #icon>
                                            <span>{{
                                                showPass ? "🙉" : "🙈"
                                            }}</span>
                                        </template>
                                    </custom-input>
                                </div>
                                <app-button
                                    class="mt-8"
                                    :disabled="!isValidInfo"
                                    :loading="isLoading"
                                    block
                                    size="large"
                                    @click="loginPassword"
                                >
                                    Iniciar
                                </app-button>
                            </div>

                            <div
                                v-if="$data.$showEnabledGoogleAuth"
                                class="flex justify-center my-2"
                            >
                                <button
                                    class="font-bold btn-a"
                                    aria-label="Cambiar opción de inicio de sesión"
                                    @click="
                                        showFederatedSignIn =
                                            !showFederatedSignIn
                                    "
                                >
                                    {{
                                        showFederatedSignIn
                                            ? "Inicia con contraseña"
                                            : "Inicia con redes"
                                    }}
                                </button>
                            </div>
                        </template>

                        <federated-sign-in-progress
                            v-else
                            :error="error"
                            @retry="loginGoogle"
                            @cancel="cancelAutoRedirect"
                        />
                    </div>
                </div>
            </div>
        </template>
        <template v-else>
            <div
                class="mx-auto min-h-screen px-2 py-8 flex flex-col justify-center"
            >
                <div
                    class="w-full sm:w-10/12 md:w-8/12 lg:w-1/2 xl:w-5/12 2xl:w-4/12 mx-auto py-2 flex flex-col justify-center mb-10"
                >
                    <div
                        class="text-center py-4 lg:py-3 px-3 shadow-lg bg-body rounded-md"
                    >
                        <span
                            class="inline-block bg-stack-primary text-white text-xs font-semibold uppercase tracking-wide rounded-full px-3 py-1 mb-2"
                        >
                            Bienvenido de nuevo
                        </span>
                        <h1
                            class="text-login-headline font-bold text-theme-base text-center mb-1"
                        >
                            {{ slogan }}
                        </h1>
                        <p class="text-theme-muted text-center text-sm mb-4">
                            Ingresa a tu cuenta
                            <span
                                class="inline-block font-semibold text-white bg-theme-primary rounded-full px-2 py-0.5"
                                >{{ $siteTitle }}</span
                            >
                        </p>
                        <small
                            v-if="showBrandFooter"
                            class="m-0 font-mono flex justify-center"
                        >
                            By&nbsp;
                            <a
                                href="https://tucatalogo.co"
                                target="_blank"
                                rel="noopener"
                            >
                                tucatalogo.co
                            </a>
                        </small>
                    </div>
                    <img
                        v-if="!autoRedirecting && !isLoading"
                        src="/img/logos/pet_logo.png"
                        loading="lazy"
                        class="mx-auto my-2 h-48 2xl:h-64 w-auto object-contain"
                        :alt="`Inicia sesión en ${$siteTitle}`"
                    />

                    <template v-if="!autoRedirecting">
                        <div
                            v-if="showFederatedSignIn"
                            class="flex flex-col items-center justify-center w-10/12 mx-auto h-28 px-2"
                        >
                            <app-button
                                size="large"
                                block
                                border
                                @click="loginGoogle"
                            >
                                <img
                                    src="@/assets/images/google.svg"
                                    height="15"
                                    width="15"
                                    alt="reload img"
                                />
                                &nbsp; &nbsp;Continua con Google
                            </app-button>
                        </div>

                        <div v-else class="px-2">
                            <div
                                v-if="!!error"
                                class="bg-theme-danger border border-danger-light text-white p-2 mb-4 rounded"
                            >
                                {{ error }}
                            </div>

                            <div class="mt-2">
                                <custom-input
                                    id="user"
                                    v-model="user"
                                    type="text"
                                    name="user"
                                    :disabled="isLoading"
                                    label="Correo"
                                    aria-label="Correo"
                                    :error="
                                        !validUser && introUser
                                            ? 'Ingrese correo o usuario valido'
                                            : ''
                                    "
                                    @blur="introUser = true"
                                    @input="lowerCaseUser"
                                    @enter="loginPassword"
                                >
                                    <template #icon> 🐵 </template>
                                </custom-input>
                            </div>
                            <div class="mt-4">
                                <custom-input
                                    v-model="password"
                                    :type="showPass ? 'text' : 'password'"
                                    label="Contraseña"
                                    aria-label="Contraseña"
                                    autocomplete="off"
                                    :disabled="isLoading"
                                    :error="
                                        !validPassword && introPassword
                                            ? 'Ingrese una contraseña valida'
                                            : ''
                                    "
                                    @click-icon="showPass = !showPass"
                                    @blur="introPassword = true"
                                    @enter="loginPassword"
                                >
                                    <template #icon>
                                        <span>{{
                                            showPass ? "🙉" : "🙈"
                                        }}</span>
                                    </template>
                                </custom-input>
                            </div>
                            <app-button
                                class="mt-8"
                                :disabled="!isValidInfo"
                                :loading="isLoading"
                                block
                                size="large"
                                @click="loginPassword"
                            >
                                Iniciar
                            </app-button>
                        </div>

                        <div
                            v-if="$data.$showEnabledGoogleAuth"
                            class="flex justify-center my-2"
                        >
                            <button
                                class="font-bold btn-a"
                                aria-label="Cambiar opción de inicio de sesión"
                                @click="
                                    showFederatedSignIn = !showFederatedSignIn
                                "
                            >
                                {{
                                    showFederatedSignIn
                                        ? "Inicia con contraseña"
                                        : "Inicia con redes"
                                }}
                            </button>
                        </div>
                    </template>

                    <federated-sign-in-progress
                        v-else
                        :error="error"
                        @retry="loginGoogle"
                        @cancel="cancelAutoRedirect"
                    />
                </div>
            </div>
        </template>

        <div class="fixed bottom-0 right-0 z-40 p-2">
            <change-theme />
        </div>
    </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";

import {
    FRANCHISE_PORTAL_DESCRIPTION,
    FRANCHISE_PORTAL_KEYWORDS,
    IS_ENABLED_GOOGLE_AUTH,
    LANDING_PLAN_INTENT_STORAGE_KEY,
    LANDING_SOURCE_STORAGE_KEY,
    LOGIN_PAGE_HEADLINE,
    LOGIN_PAGE_TITLE,
    MARKETPLACE_PORTAL_SLOGAN,
    MARKETPLACE_PORTAL_URL,
    SHOW_LOGIN_IMAGE,
    STACK_NAME
} from "@/common/constants";
import { emailRegex, passwordRegex } from "@/common/lib/regex";

import ChangeTheme from "@/components/layout/ChangeTheme.vue";
import CustomInput from "@/components/utils/CustomInput.vue";
import FederatedSignInProgress from "@/components/utils/FederatedSignInProgress.vue";

export default {
    name: "LoginPage",
    components: { ChangeTheme, CustomInput, FederatedSignInProgress },
    data: () => ({
        user: "",
        password: "",
        $showEnabledGoogleAuth: IS_ENABLED_GOOGLE_AUTH,
        showFederatedSignIn: IS_ENABLED_GOOGLE_AUTH,
        introUser: false,
        introPassword: false,
        slogan: LOGIN_PAGE_HEADLINE,
        error: "",
        showPass: false,
        validUser: false,
        validPassword: false,
        isLoading: false,
        autoRedirecting: false,
        showLoginImage: SHOW_LOGIN_IMAGE,
        showBrandFooter: STACK_NAME !== "tucatalogo"
    }),
    metaInfo: {
        title: LOGIN_PAGE_TITLE,
        meta: [
            {
                name: "description",
                content: FRANCHISE_PORTAL_DESCRIPTION
            },
            {
                name: "keywords",
                content: FRANCHISE_PORTAL_KEYWORDS
            },
            {
                name: "og:title",
                content: `${LOGIN_PAGE_TITLE} - ${MARKETPLACE_PORTAL_SLOGAN}`
            },
            {
                name: "og:image:secure_url",
                content: `${MARKETPLACE_PORTAL_URL}/img/index.jpg`
            },
            { name: "og:image:height", content: "627" },
            { name: "og:image:width", content: "1200" },
            {
                name: "og:image:alt",
                content: "Crea tu catálogo en línea y comparte tu URL"
            },
            { name: "og:image:type", content: "image/jpg" }
        ]
    },
    computed: {
        ...mapState("control", ["loading"]),
        ...mapState("commons", ["postLoginRedirect"]),
        isValidInfo() {
            return this.validUser && this.validPassword && !this.loading;
        }
    },
    watch: {
        user(val) {
            this.validUser = emailRegex.test(val);
        },
        password(val) {
            this.validPassword = passwordRegex.test(val);
        }
    },
    created() {
        this.CLEAR_AUTH();

        const { federatedAutoLogin, period, plan, source } = this.$route.query;
        if (source) {
            sessionStorage.setItem(LANDING_SOURCE_STORAGE_KEY, source);
            this.$analytics.trackEvent("landing_visit", { source });
        }
        if (plan) {
            sessionStorage.setItem(
                LANDING_PLAN_INTENT_STORAGE_KEY,
                JSON.stringify({ plan, period })
            );
        }

        if (federatedAutoLogin === "true" && IS_ENABLED_GOOGLE_AUTH) {
            this.autoRedirecting = true;
            this.loginGoogle();
        }
    },
    methods: {
        ...mapMutations("auth", ["CLEAR_AUTH"]),
        ...mapMutations("commons", ["CLEAR_POST_LOGIN_REDIRECT"]),
        ...mapActions("commons", ["listOwnMerchants"]),
        ...mapActions("auth", ["signInPassword", "signInGoogle"]),
        lowerCaseUser() {
            this.user = this.user.toLowerCase();
        },
        async loginPassword() {
            if (!this.isValidInfo) return;
            this.isLoading = true;
            this.error = "";
            try {
                const { callback, tenantId } = await this.signInPassword({
                    email: this.user,
                    password: this.password
                });

                if (
                    tenantId &&
                    this.$ability.hasPermission("merchant:ListOwn")
                ) {
                    await this.listOwnMerchants({
                        required: true
                    });
                }
                if (this.user?.sub) {
                    this.$analytics.identifyUser(this.user.sub, {
                        $email: this.user.email,
                        $name: [this.user.name, this.user.lastName]
                            .filter(Boolean)
                            .join(" ")
                    });
                }
                const source = sessionStorage.getItem(
                    LANDING_SOURCE_STORAGE_KEY
                );
                this.$analytics.trackEvent("user_logged_in", {
                    method: "password",
                    ...(source ? { source } : {})
                });
                sessionStorage.removeItem(LANDING_SOURCE_STORAGE_KEY);
                const redirectTo =
                    callback === "/app" && this.postLoginRedirect
                        ? this.postLoginRedirect
                        : callback;
                this.CLEAR_POST_LOGIN_REDIRECT();
                this.$router.push(redirectTo);
                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                console.error(error);
                this.error = error.message;
                this.$analytics.trackEvent("login_failed", {
                    method: "password"
                });
            }
        },
        async loginGoogle() {
            this.isLoading = true;
            this.error = "";
            this.autoRedirecting = true;
            try {
                await this.signInGoogle();
                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                this.error = "Ocurrió un error al iniciar sesión con Google.";
                console.error(error);
            }
        },
        cancelAutoRedirect() {
            this.error = "";
            this.autoRedirecting = false;
        }
    }
};
</script>
